import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { warehouseApi } from '../../services/api';
import { BuildingOfficeIcon, PlusIcon, PencilIcon, TrashIcon, FunnelIcon } from '@heroicons/react/24/outline';

interface CategoryCount {
  category_id: number;
  category_name: string;
  category_slug: string;
  available: number;
  total: number;
}

interface WarehouseWithCategories {
  id: string;
  code: string;
  name: string;
  categories: CategoryCount[];
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

const WarehouseList: React.FC = () => {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState<WarehouseWithCategories[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);

  // Função para abreviar nomes de categorias
  const abbreviateCategoryName = (name: string): string => {
    // Regras de abreviação
    const abbreviations: Record<string, string> = {
      'Banheiros Químicos': 'Banh. Quím.',
      'Equipamentos Pesados': 'Equip. Pesados',
      'Veículos': 'Veículos',
      'Equipamentos Leves': 'Equip. Leves',
      'Ferramentas': 'Ferram.',
      'Acessórios': 'Acess.',
    };

    // Se tem abreviação específica, usar
    if (abbreviations[name]) {
      return abbreviations[name];
    }

    // Caso contrário, tentar abreviar automaticamente
    const words = name.split(' ');
    if (words.length > 2) {
      // Pega primeira palavra completa + iniciais das outras
      return words[0] + ' ' + words.slice(1).map(w => w[0] + '.').join('');
    } else if (name.length > 15) {
      // Se muito longo, truncar
      return name.substring(0, 13) + '...';
    }

    return name;
  };

  useEffect(() => {
    loadWarehouses();
  }, [selectedCategories]);

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const response = await warehouseApi.getWarehousesWithCategories(
        selectedCategories.length > 0 ? selectedCategories : undefined
      );
      setWarehouses(response.warehouses);
      setAllCategories(response.available_categories);

      // Se nenhuma categoria selecionada, selecionar todas por padrão
      if (selectedCategories.length === 0 && response.available_categories.length > 0) {
        setSelectedCategories(response.available_categories.map(c => c.id));
      }
    } catch (err) {
      setError('Erro ao carregar depósitos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, code: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir o depósito "${code}"?`)) {
      return;
    }

    try {
      await warehouseApi.deleteWarehouse(id);
      setWarehouses(prev => prev.filter(w => w.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erro ao excluir depósito');
    }
  };

  const toggleCategory = (categoryId: number) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const selectAllCategories = () => {
    setSelectedCategories(allCategories.map(c => c.id));
  };

  const deselectAllCategories = () => {
    setSelectedCategories([]);
  };

  const filteredWarehouses = warehouses.filter(warehouse =>
    warehouse.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pegar apenas categorias selecionadas para exibir
  const displayCategories = allCategories.filter(cat =>
    selectedCategories.includes(cat.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-base font-bold text-gray-900 flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 mr-2 text-blue-600" />
            Depósitos
          </h1>
          <p className="text-gray-600 mt-1">Gerencie os depósitos e quantidades por categoria</p>
        </div>
        <button
          onClick={() => navigate('/warehouses/new')}
          className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Novo Depósito
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filtros */}
      <div className="mb-4 space-y-3">
        {/* Busca */}
        <input
          type="text"
          placeholder="Buscar por código..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Filtro de Categorias */}
        <div className="bg-white border border-gray-300 rounded-md p-3">
          <button
            onClick={() => setShowCategoryFilter(!showCategoryFilter)}
            className="flex items-center text-gray-700 font-medium hover:text-gray-900 w-full"
          >
            <FunnelIcon className="h-4 w-4 mr-1.5" />
            Filtrar Categorias ({selectedCategories.length}/{allCategories.length})
          </button>

          {showCategoryFilter && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex gap-2 mb-3">
                <button
                  onClick={selectAllCategories}
                  className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Selecionar Todas
                </button>
                <button
                  onClick={deselectAllCategories}
                  className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Limpar
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {allCategories.map(category => (
                  <label
                    key={category.id}
                    className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category.id)}
                      onChange={() => toggleCategory(category.id)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{category.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white shadow-sm rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                Código
              </th>
              {displayCategories.map(category => (
                <th
                  key={category.id}
                  className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  title={category.name}
                >
                  {abbreviateCategoryName(category.name)}
                </th>
              ))}
              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredWarehouses.length === 0 ? (
              <tr>
                <td colSpan={displayCategories.length + 2} className="px-2 py-1.5 text-center text-xs text-gray-500">
                  Nenhum depósito encontrado
                </td>
              </tr>
            ) : (
              filteredWarehouses.map((warehouse) => (
                <tr key={warehouse.id} className="hover:bg-gray-50">
                  <td className="px-2 py-1.5 whitespace-nowrap sticky left-0 bg-white z-10">
                    <span className="text-xs font-medium text-gray-900">{warehouse.code}</span>
                  </td>
                  {displayCategories.map(category => {
                    const catData = warehouse.categories.find(c => c.category_id === category.id);
                    const available = catData?.available || 0;
                    const total = catData?.total || 0;

                    return (
                      <td key={category.id} className="px-2 py-1.5 text-center whitespace-nowrap">
                        {total > 0 ? (
                          <div className="inline-flex flex-col items-center">
                            <span className="text-xs font-semibold text-gray-900">
                              {available}/{total}
                            </span>
                            <span className="text-[10px] text-gray-500 leading-tight">
                              {available === total ? 'disp.' : `${total - available} uso`}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 whitespace-nowrap text-right sticky right-0 bg-white z-10">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => navigate(`/warehouses/edit/${warehouse.id}`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Editar"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(warehouse.id, warehouse.code)}
                        className="text-red-600 hover:text-red-900"
                        title="Excluir"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-gray-600">
        Total: {filteredWarehouses.length} depósito(s) | Categorias exibidas: {displayCategories.length}
      </div>
    </div>
  );
};

export default WarehouseList;
